import React, {useEffect, useState} from "react";
import styles, {C} from "../styles";
import {apiGetCompleteRegistration, apiCompleteRegistration} from "../api/waitlist";
import {apiGetPseudoSuggestions} from "../api/auth";
import {validatePassword} from "../utils";
import {trackSignupSucceeded} from "../analytics";
import PseudoWaitlistNotice from "../components/PseudoWaitlistNotice";
import CguAcceptance from "../components/CguAcceptance";
import EmailVerificationNotice from "../components/EmailVerificationNotice";

export default function CompleteRegistrationPage({token, onGoToLogin}) {
    const [email, setEmail] = useState(null);
    const [error, setError] = useState(null);
    const [loading, setLoading] = useState(true);
    const [suggestions, setSuggestions] = useState([]);
    const [selectedPseudo, setSelectedPseudo] = useState(null);
    const [loadingSuggestions, setLoadingSuggestions] = useState(true);
    const [password, setPassword] = useState("");
    const [submitError, setSubmitError] = useState("");
    const [successMessage, setSuccessMessage] = useState("");
    const [cguAccepted, setCguAccepted] = useState(false);

    const pwdCheck = validatePassword(password);
    const canSubmit = !!(selectedPseudo && pwdCheck.valid && cguAccepted);

    useEffect(() => {
        apiGetCompleteRegistration(token).then(data => {
            if (data.error) setError(data.error);
            else setEmail(data.email);
            setLoading(false);
        });
    }, [token]);

    useEffect(() => {
        if (loading || error) return;
        setLoadingSuggestions(true);
        apiGetPseudoSuggestions().then(data => {
            setSuggestions(Array.isArray(data) ? data : []);
            setLoadingSuggestions(false);
        });
    }, [loading, error]);

    async function handleSubmit() {
        setSubmitError("");
        const data = await apiCompleteRegistration(token, selectedPseudo.pseudo, password, cguAccepted);
        if (data.error) {
            setSubmitError(data.error);
            return;
        }
        trackSignupSucceeded();
        setSuccessMessage(data.message);
    }

    if (loading) {
        return (
            <div style={{...styles.page, display: "flex", justifyContent: "center", alignItems: "center", minHeight: "100vh"}}>
                <p style={{color: C.textMuted, fontSize: 16}}>Chargement...</p>
            </div>
        );
    }

    if (error) {
        return (
            <div style={{...styles.page, display: "flex", justifyContent: "center", alignItems: "center", minHeight: "100vh"}}>
                <div style={{...styles.card, maxWidth: 440, textAlign: "center", padding: 32}}>
                    <div style={{fontSize: 48, marginBottom: 12}}>😕</div>
                    <h2 style={{margin: "0 0 8px", fontFamily: "'Fraunces', serif"}}>Lien invalide</h2>
                    <p style={{color: C.textMuted}}>{error}</p>
                </div>
            </div>
        );
    }

    if (successMessage) {
        return (
            <div style={{...styles.page, display: "flex", justifyContent: "center", alignItems: "center", minHeight: "100vh"}}>
                <div style={{...styles.card, maxWidth: 480, padding: 32}}>
                    <EmailVerificationNotice email={email} pseudo={selectedPseudo?.pseudo} nextStep="connectez-vous"/>
                    <div style={{textAlign: "center"}}>
                        <button onClick={onGoToLogin} style={styles.primaryButton}>Se connecter</button>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div style={{...styles.page, display: "flex", justifyContent: "center", alignItems: "center", minHeight: "100vh"}}>
            <div style={{...styles.card, maxWidth: 440, padding: 32}}>
                <h2 style={{margin: "0 0 4px", fontFamily: "'Fraunces', serif"}}>Finaliser mon inscription</h2>
                <p style={{color: C.textMuted, marginBottom: 16}}>{email}</p>

                {loadingSuggestions ? (
                    <p style={{fontSize: 13, color: C.textMuted}}>Génération des suggestions…</p>
                ) : suggestions.length === 0 ? (
                    <PseudoWaitlistNotice email={email} onJoined={() => {}} />
                ) : (
                    <>
                        <p style={{fontSize: 13, color: C.textMuted, margin: "0 0 8px"}}>
                            Choisissez votre pseudo — il préserve votre anonymat
                        </p>
                        <div style={{display: "flex", gap: 8, marginBottom: 8, flexWrap: "wrap"}}>
                            {suggestions.map(s => (
                                <button
                                    key={s.pseudo}
                                    type="button"
                                    onClick={() => setSelectedPseudo(s)}
                                    style={{
                                        flex: "1 1 120px",
                                        padding: "12px 8px",
                                        borderRadius: 12,
                                        border: selectedPseudo?.pseudo === s.pseudo ? `2px solid ${C.primary}` : `1px solid ${C.border}`,
                                        background: `radial-gradient(circle at 35% 30%, ${s.couleur}CC, ${s.couleur}, ${s.couleur}88)`,
                                        color: "white",
                                        cursor: "pointer",
                                        textAlign: "center",
                                    }}
                                >
                                    <div style={{fontSize: 28, marginBottom: 4}}>{s.emoji}</div>
                                    <div style={{fontSize: 13, fontWeight: 700, textShadow: "0 1px 2px rgba(0,0,0,0.3)"}}>{s.pseudo}</div>
                                </button>
                            ))}
                        </div>

                        <input
                            type="password"
                            placeholder="Mot de passe"
                            value={password}
                            onChange={e => setPassword(e.target.value)}
                            style={styles.input}
                        />
                        {password.length > 0 && (
                            <div style={{display: "grid", gridTemplateColumns: "1fr 1fr", gap: "2px 12px", fontSize: 12, marginBottom: 10}}>
                                {[
                                    [pwdCheck.minLength, "8 caractères min."],
                                    [pwdCheck.hasUpper, "1 majuscule"],
                                    [pwdCheck.hasDigit, "1 chiffre"],
                                    [pwdCheck.hasSpecial, "1 caractère spécial"],
                                ].map(([ok, label]) => (
                                    <span key={label} style={{color: ok ? "#22C55E" : C.textMuted}}>
                                        {ok ? "✅" : "○"} {label}
                                    </span>
                                ))}
                            </div>
                        )}

                        <CguAcceptance accepted={cguAccepted} onChange={setCguAccepted}/>

                        {submitError && <p style={{color: C.danger, fontSize: 13, margin: "0 0 10px"}}>{submitError}</p>}

                        <button onClick={handleSubmit} disabled={!canSubmit} style={{...styles.primaryButton, width: "100%", opacity: canSubmit ? 1 : 0.5}}>
                            Finaliser mon inscription
                        </button>
                    </>
                )}
            </div>
        </div>
    );
}
